import {Loaded} from 'Core/models/Async';
import {Defaultable, eqDefault} from 'Core/models/Default';
import {createValidatableEmptyEmail, eqValidatableEmail, ValidatableEmail, ValidatedEmail} from 'Core/models/Email';
import {EmptyString, eqStringUnion, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {
  createValidatableEmptyString,
  eqValidatableNonEmptyString,
  ValidatableNonEmptyString,
  ValidatedNonEmptyString,
} from 'Core/models/ValidatableNonEmptyString';
import {
  createValidatableEmptyPhone,
  EmptyPhoneNumber,
  eqValidatablePhone,
  isEmptyPhoneNumber,
  ValidatablePhone,
  ValidatedPhoneNumber,
} from 'Core/models/PhoneNumber';
import {eqValid, Validatable} from 'Core/models/Valid';
import {isValid} from 'Core/models/Validatable';
import * as E from 'fp-ts/lib/Either';
import {Eq, eqBoolean, getStructEq, eqString} from 'fp-ts/lib/Eq';
import {NonEmptyArray, getEq} from 'fp-ts/lib/NonEmptyArray';
import {RoleBase} from 'Users/api';
import {isNonEmpty} from 'fp-ts/lib/Array';

export type EnrollTrackUser = Validatable<Defaultable<InvalidEnrollTrackUser>, ValidEnrollTrackUser>;

export type InvalidEnrollTrackUser = {
  firstName: StringUnion;
  lastName: StringUnion;
  username: ValidatableNonEmptyString;
  phoneNumber: ValidatablePhone;
  email: ValidatableEmail;
  isUserSupport: boolean;
  id?: string;
  roles: string[];
};

export const validateEnrollTrackUser = (user: ValidEnrollTrackUser | InvalidEnrollTrackUser) =>
  ValidEnrollTrackUser.create(
    user.firstName,
    user.lastName,
    user.username,
    user.phoneNumber,
    user.email,
    user.isUserSupport,
    user.roles,
    user.id
  );

export class ValidEnrollTrackUser {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    username: ValidatableNonEmptyString,
    phoneNumber: ValidatablePhone,
    email: ValidatableEmail,
    isUserSupport: boolean,
    roles: string[],
    id?: string
  ): EnrollTrackUser {
    return firstName instanceof NonEmptyString &&
      lastName instanceof NonEmptyString &&
      isNonEmpty(roles) &&
      isValid(email) &&
      isValid(username) &&
      (isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber))
      ? E.right(new ValidEnrollTrackUser(firstName, lastName, username, phoneNumber, email, isUserSupport, roles, id))
      : E.left(
          E.right({
            firstName,
            lastName,
            username,
            phoneNumber,
            email,
            isUserSupport,
            roles,
            id,
          } as InvalidEnrollTrackUser)
        );
  }

  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: ValidatedPhoneNumber | Loaded<E.Left<EmptyPhoneNumber>>,
    public readonly email: ValidatedEmail,
    public readonly isUserSupport: boolean,
    public readonly roles: NonEmptyArray<string>,
    public readonly id?: string
  ) {}
}

const eqRole = (getStructEq({
  id: eqString,
  //domainEvents?: Array<object>
  applicationRole: eqString,
  scopes: getEq(eqString),
}) as unknown) as Eq<RoleBase>;

export const eqEnrollTrackUser: Eq<InvalidEnrollTrackUser | ValidEnrollTrackUser> = getStructEq<
  InvalidEnrollTrackUser | ValidEnrollTrackUser
>({
  email: eqValidatableEmail,
  firstName: eqStringUnion,
  lastName: eqStringUnion,
  phoneNumber: eqValidatablePhone,
  username: eqValidatableNonEmptyString,
  isUserSupport: eqBoolean,
  roles: getEq(eqString),
});

export const eqValidatableEnrollTrackUser: Eq<EnrollTrackUser> = eqValid(
  eqDefault(eqEnrollTrackUser),
  eqEnrollTrackUser
);

export const EmptyEnrollTrackUser: EnrollTrackUser = E.left(
  E.left({
    email: createValidatableEmptyEmail(),
    firstName: new EmptyString(),
    lastName: new EmptyString(),
    phoneNumber: createValidatableEmptyPhone(),
    username: createValidatableEmptyString(),
    isUserSupport: false,
    roles: [],
  })
);
